<template>
  <Modal
    persistent
    title="Confirm User Request Rejection"
  >
    <QCardSection>
      <template v-if="newUserRows">
        <InputSelect
          v-model="newUserReason"
          class="q-mt-sm"
          emit-value
          label="Select New Users Rejection Reason"
          option-label="label"
          option-prop="value"
          :options="rejectReasons.newUser"
          placeholder="Select Reason"
        />
        <p
          v-if="newUserReason"
          class="q-ma-sm soft"
        >
          Reason Text: {{ newUserReason }}
        </p>
      </template>
      <template v-if="activateUserRows">
        <InputSelect
          v-model="activateReason"
          class="q-mt-sm"
          emit-value
          label="Select Activate Rejection Reason"
          option-label="label"
          option-prop="value"
          :options="rejectReasons.activate"
          placeholder="Select Reason"
        />
        <p
          v-if="activateReason"
          class="q-ma-sm soft"
        >
          Reason Text: {{ activateReason }}
        </p>
      </template>
      <template v-if="deactivateUserRows">
        <InputSelect
          v-model="deactivateReason"
          class="q-mt-sm"
          emit-value
          label="Select Deactivate Rejection Reason"
          option-label="label"
          option-prop="value"
          :options="rejectReasons.deactivate"
          placeholder="Select Reason"
        />
        <p
          v-if="deactivateReason && deactivateReason !== 'Other'"
          class="q-ma-sm soft"
        >
          Reason Text: {{ deactivateReason }}
        </p>
        <QInput
          v-if="deactivateReason === 'Other'"
          v-model="customReason"
          class="q-my-md"
          dense
          filled
          label="Reason"
          placeholder="Reason"
          :rules="[(val) => (val && val.length > 0) || 'Must set reason']"
          type="textarea"
        />
      </template>
    </QCardSection>
    <template #buttons="{ close }">
      <BaseButton
        class="q-ma-sm"
        flat
        label="Cancel"
        @click="close"
      />
      <BaseButton
        class="q-ma-sm"
        color="primary"
        :disabled="submitDisabled"
        label="Reject"
        @click="submit"
      />
    </template>
  </Modal>
</template>

<script>
import { groupBy } from "lodash-es";
import { storeToRefs } from "pinia";

import { BaseButton, InputSelect } from "shared/components/base";
import Modal from "shared/components/core/modals/Modal.vue";
import { pluralize } from "shared/helpers/string";
import { useGlobalStore } from "shared/stores/global";

export default {
  name: "RejectUserRequestModal",
  components: {
    BaseButton,
    InputSelect,
    Modal,
  },
  props: {
    userRequests: {
      type: [Object, Array],
      required: true,
    },
  },
  emits: ["reject"],
  setup() {
    const globalStore = useGlobalStore();
    const { appConfig } = storeToRefs(globalStore);

    return { appConfig };
  },
  data() {
    return {
      activateReason: "",
      deactivateReason: "",
      customReason: "",
      newUserReason: "",
    };
  },
  computed: {
    rejectReasons() {
      return {
        newUser: [
          {
            label: "Reached User Allowance",
            value:
              "Your organisation has reached its user allowance as outlined in the SLA.",
          },
          {
            label: "Email Taken",
            value:
              "This email address is already in use by a member of your team.",
          },
          {
            label: "Mobile Taken",
            value:
              "This phone number is already in use by another Streem user.",
          },
          {
            label: "Group Email Address",
            value: `${this.appConfig.platformName} is not licensed to provide access to the platform using group email addresses. Please ensure all email addresses are unique to an individual team member.`,
          },
          {
            label: "Incorrect Domain",
            value: "This email address is outside your organisation’s domain.",
          },
        ],
        activate: [
          {
            label: "Requested by Mistake",
            value:
              "Please contact your account manager if you believe this is incorrect.",
          },
        ],
        deactivate: [
          {
            label: "Key Contact",
            value:
              "The key contact for your organisation cannot be deactivated. Please contact your account manager to nominate a new key contact",
          },
          {
            label: "Scheduled Reports",
            value:
              "There are active scheduled reports associated with this user. Please contact your account manager.",
          },
          {
            label: "Curated Email Reports",
            value:
              "There are active Curated Email Reports associated with this user. Please contact your account manager.",
          },
          {
            label: "Requested by Mistake",
            value:
              "Please contact your account manager if you believe this is incorrect.",
          },
          {
            label: "Other",
            value: "Other",
          },
        ],
      };
    },
    requestsByType() {
      return groupBy(this.userRequests, "action");
    },
    activateUserRows() {
      return this.requestsByType.activate;
    },
    newUserRows() {
      return this.requestsByType.new_user;
    },
    deactivateUserRows() {
      return this.requestsByType.deactivate;
    },
    deactivateReasonEmpty() {
      return (
        !this.deactivateReason ||
        (this.deactivateReason === "Other" && !this.customReason)
      );
    },
    submitDisabled() {
      return (
        (Boolean(this.newUserRows) && !this.newUserReason) ||
        (Boolean(this.deactivateUserRows) && this.deactivateReasonEmpty) ||
        (Boolean(this.activateUserRows) && !this.activateReason)
      );
    },
  },
  methods: {
    submit() {
      const actions = [];

      if (this.newUserRows) {
        const newUsersCount = this.newUserRows.length;

        actions.push(`
          <strong class="text-ds-green-5">
            ${this.newUserRows.length} new user ${pluralize(
              newUsersCount,
              "request"
            )} will be rejected
          </strong>
        `);
      }

      if (this.deactivateUserRows) {
        const deactivatedUsersCount = this.deactivateUserRows.length;

        actions.push(`
          <strong class="text-ds-red-5">
            ${this.deactivateUserRows.length} deactivate ${pluralize(
              deactivatedUsersCount,
              "request"
            )} will be rejected
          </strong>
        `);
      }

      if (this.activateUserRows) {
        const activatedUsersCount = this.activateUserRows.length;

        actions.push(`
          <strong class="text-ds-red-5">
            ${this.activateUserRows.length} activate ${pluralize(
              activatedUsersCount,
              "request"
            )} will be rejected
          </strong>
        `);
      }

      this.$dialog({
        theme: "danger",
        title: "Confirm Rejection",
        html: true,
        message: `<p>By proceeding, ${actions.join(" and ")}.</p>`,
        confirmLabel: `Reject ${this.userRequests.length} Requests`,
      }).onOk(() => {
        const requests = this.userRequests.map((request) => {
          let rejectReason = this.newUserReason;

          if (request.action === "deactivate") {
            rejectReason = this.customReason || this.deactivateReason;
          } else if (request.action === "activate") {
            rejectReason = this.activateReason;
          }

          return {
            ...request,
            rejectReason,
          };
        });

        this.$emit("reject", requests);
      });
    },
  },
};
</script>

<style lang="scss" scoped>
:deep(.input-wrapper) {
  padding-left: 8px;
}
</style>
